/**
 * Client-side block editor model + a plain-HTML renderer. Mirrors the
 * leaf-block shapes in `packages/core/src/templates/schema.ts` (text,
 * image, button, product, divider, footer — no nested sections, which
 * that schema only allows one level deep and this simple list editor
 * doesn't need). The API sends/stores the final rendered HTML (plus the
 * block list itself, opaquely, as `blockJson: { blocks }` on templates and
 * campaigns so they can be reopened) — this renderer produces that HTML,
 * entirely in the browser, no `mjml-browser` dependency needed for a
 * table/div-based admin preview.
 */

import {
  MIN_SENDER_DETAILS_LENGTH,
  MISSING_SENDER_MESSAGE,
  SENDER_DETAILS_CLASS,
} from '@nopain-newsletter/core/sections';
import {
  DEFAULT_FEED_COUNT,
  DEFAULT_FEED_LABEL,
  MAX_FEED_COLUMNS,
  ensureDeviceCss,
  parseShowExpression,
  productFeedDirective,
  sharedBlockDirective,
  type ProductFeed,
  wrapDevice,
  wrapShow,
  type DeviceRule,
} from '@nopain-newsletter/core/blocks';

/**
 * Optional K4 settings on every block except the footer: a show/hide rule
 * (`language=de ordered=no`, see core `blocks/showRules.ts`) and a device
 * (desktop only / mobile only). Absent = shown to everyone on every device,
 * which renders exactly as before these fields existed.
 */
interface BlockOptions {
  show?: string;
  device?: DeviceRule;
}

export type Block =
  | ({ type: 'text'; id: string; text: string } & BlockOptions)
  | ({ type: 'image'; id: string; src: string; alt: string } & BlockOptions)
  | ({ type: 'button'; id: string; label: string; href: string } & BlockOptions)
  | ({
      type: 'product';
      id: string;
      name: string;
      price: string;
      imageSrc: string;
      href: string;
    } & BlockOptions)
  | ({ type: 'divider'; id: string } & BlockOptions)
  /** Static "offer ends" text. No live timer: email cannot run scripts. */
  | ({ type: 'countdown'; id: string; heading: string; endsOn: string } & BlockOptions)
  /** Shows the contact's personal single-use code (`{{ coupon_code }}`). */
  | ({ type: 'coupon'; id: string; heading: string; code: string; note: string } & BlockOptions)
  /** Static review stars with a quote; no live Trustpilot widget. */
  | ({
      type: 'reviews';
      id: string;
      stars: number;
      quote: string;
      source: string;
    } & BlockOptions)
  /** An image that links to the video (email cannot play video). */
  | ({
      type: 'video';
      id: string;
      thumbnailSrc: string;
      alt: string;
      href: string;
    } & BlockOptions)
  /** A block saved once and reused (`{{np_block <id>}}`, core `blocks/sharedBlocks.ts`). The name is only for display. */
  | ({ type: 'shared'; id: string; sharedBlockId: string; name: string } & BlockOptions)
  /** Shop products from the catalogue (`{{np_products ...}}`, core `blocks/productFeed.ts`), filled in when the mail is prepared. */
  | ({ type: 'products'; id: string; feed: ProductFeed } & BlockOptions)
  | {
      type: 'footer';
      id: string;
      /** "Sender details" in the UI (who is sending, e.g. "NoPain® Cream"); the key keeps its old name for saved documents. */
      companyAddress: string;
      unsubscribeHref: string;
      /** Optional legal notice / Impressum link, shown as "Legal notice" before "Unsubscribe". */
      legalNoticeHref?: string;
    };

/** Brand kit values a new footer starts from (see {@link createBlock}). */
export interface FooterDefaults {
  /** True when the brand kit has a legal notice link: new footers then link `{{ legal_notice_url }}`. */
  hasLegalNoticeUrl?: boolean;
}

export { MIN_SENDER_DETAILS_LENGTH, MISSING_SENDER_MESSAGE };

export const BLOCK_LABELS: Record<Block['type'], string> = {
  text: 'Text',
  image: 'Image',
  button: 'Button',
  product: 'Product',
  divider: 'Divider',
  countdown: 'Offer ends',
  coupon: 'Coupon',
  reviews: 'Reviews',
  video: 'Video',
  shared: 'Shared block',
  products: 'Product feed',
  footer: 'Footer',
};

let counter = 0;
export function newBlockId(): string {
  counter += 1;
  return `b-${Date.now()}-${counter}`;
}

export function createBlock(type: Block['type'], footerDefaults: FooterDefaults = {}): Block {
  const id = newBlockId();
  switch (type) {
    case 'text':
      return { type, id, text: 'Write your text here.' };
    case 'image':
      return { type, id, src: 'https://placehold.co/600x200', alt: '' };
    case 'button':
      return { type, id, label: 'Read more', href: 'https://example.com' };
    case 'product':
      return {
        type,
        id,
        name: '[Product name]',
        price: '€199',
        imageSrc: 'https://placehold.co/200x200',
        href: 'https://example.com',
      };
    case 'divider':
      return { type, id };
    case 'countdown':
      return { type, id, heading: 'Offer ends', endsOn: 'Sunday at midnight' };
    case 'coupon':
      return {
        type,
        id,
        heading: 'Your personal code',
        code: '{{ coupon_code }}',
        note: 'Valid once. Enter it at checkout.',
      };
    case 'reviews':
      return { type, id, stars: 5, quote: 'Write a customer quote here.', source: 'Trustpilot' };
    case 'video':
      return {
        type,
        id,
        thumbnailSrc: 'https://placehold.co/600x340',
        alt: '',
        href: 'https://example.com',
      };
    case 'shared':
      return { type, id, sharedBlockId: '', name: '' };
    case 'products':
      return {
        type,
        id,
        feed: {
          source: 'newest',
          count: DEFAULT_FEED_COUNT,
          columns: MAX_FEED_COLUMNS,
          label: DEFAULT_FEED_LABEL,
          lang: 'en',
          ids: [],
        },
      };
    case 'footer':
      return {
        type,
        id,
        // The brand kit's sender details, filled in at send time; an empty
        // line would fail the server's sender check on the very first save.
        companyAddress: '{{ company_address }}',
        unsubscribeHref: '{{ unsubscribe_url }}',
        ...(footerDefaults.hasLegalNoticeUrl ? { legalNoticeHref: '{{ legal_notice_url }}' } : {}),
      };
  }
}

/** The starting document for a brand-new template or campaign. */
export function seedBlocks(footerDefaults: FooterDefaults = {}): Block[] {
  return [createBlock('text'), createBlock('footer', footerDefaults)];
}

/**
 * `blockJson` is stored opaquely by the API (docs/api-ui.md "Skabeloner"
 * and "Kampagner") as `{ blocks: Block[] }` — this is the one place that
 * assumes that shape back into `Block[]`. Returns `null` when the value
 * doesn't look like a saved document (missing, legacy, foreign), so each
 * caller decides what a missing document means.
 */
export function blocksFromJson(blockJson: unknown): Block[] | null {
  if (
    blockJson &&
    typeof blockJson === 'object' &&
    Array.isArray((blockJson as { blocks?: unknown }).blocks)
  ) {
    return (blockJson as { blocks: Block[] }).blocks;
  }
  return null;
}

export function validateBlocks(blocks: Block[]): string[] {
  const errors: string[] = [];
  if (blocks.length === 0) errors.push('The template is empty.');
  const footer = blocks.find((b): b is Extract<Block, { type: 'footer' }> => b.type === 'footer');
  if (blocks.some((b) => b.type === 'shared' && !b.sharedBlockId)) {
    errors.push('A shared block is not chosen yet. Pick one or remove the block.');
  }
  if (
    blocks.some(
      (b) => b.type === 'products' && b.feed.source === 'picked' && b.feed.ids.length === 0,
    )
  ) {
    errors.push('A product feed with hand-picked products has none picked yet.');
  }
  if (!footer) {
    errors.push(
      'The template is missing a footer block. A footer with sender details and an unsubscribe link is required.',
    );
  } else if (footer.companyAddress.trim().length < MIN_SENDER_DETAILS_LENGTH) {
    errors.push(MISSING_SENDER_MESSAGE);
  }
  return errors;
}

/** Why a block's show/hide rule is invalid, or null when it is empty or fine. */
export function showRuleError(block: Block): string | null {
  if (block.type === 'footer' || !block.show?.trim()) return null;
  const r = parseShowExpression(block.show);
  return r.ok ? null : r.error;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** One block's HTML — exported so the campaign editor's live preview can wrap each block for hover/flash highlighting. */
export function blockToHtml(block: Block): string {
  const html = blockBodyHtml(block);
  // The footer (unsubscribe link) is never hidden, whatever its data says.
  if (block.type === 'footer') return html;
  const device = wrapDevice(html, block.device ?? 'all');
  return block.show?.trim() && !showRuleError(block) ? wrapShow(device, block.show) : device;
}

function stars(n: number): string {
  const full = Math.max(0, Math.min(5, Math.round(n)));
  return '&#9733;'.repeat(full) + '&#9734;'.repeat(5 - full);
}

function blockBodyHtml(block: Block): string {
  switch (block.type) {
    case 'countdown':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;"><tr><td align="center" style="background:#FEF3C7;padding:14px 18px;border-radius:8px;font-family:sans-serif;color:#18181B;"><div style="font-size:12px;letter-spacing:1px;text-transform:uppercase;color:#92400E;">${escapeHtml(block.heading)}</div><div style="font-size:20px;font-weight:bold;line-height:1.3;">${escapeHtml(block.endsOn)}</div></td></tr></table>`;
    case 'coupon':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;"><tr><td align="center" style="border:2px dashed #5457D6;padding:16px;border-radius:8px;font-family:sans-serif;color:#18181B;"><div style="font-size:13px;color:#52525B;">${escapeHtml(block.heading)}</div><div style="font-size:24px;font-weight:bold;letter-spacing:2px;margin:6px 0;">${escapeHtml(block.code)}</div><div style="font-size:12px;color:#52525B;">${escapeHtml(block.note)}</div></td></tr></table>`;
    case 'reviews':
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;"><tr><td align="center" style="padding:12px;font-family:sans-serif;color:#18181B;"><div style="font-size:22px;color:#00B67A;" aria-label="${Math.max(0, Math.min(5, Math.round(block.stars)))} out of 5 stars">${stars(block.stars)}</div><div style="font-size:15px;line-height:1.5;font-style:italic;margin:6px 0;">${escapeHtml(block.quote)}</div><div style="font-size:12px;color:#52525B;">${escapeHtml(block.source)}</div></td></tr></table>`;
    case 'shared':
      // Resolved server-side when the mail is prepared; the previews expand it from the loaded blocks.
      return block.sharedBlockId ? sharedBlockDirective(block.sharedBlockId) : '';
    case 'products':
      // Filled with the current products when the mail is prepared; the previews expand it from the loaded catalogue.
      return block.feed.source === 'picked' && block.feed.ids.length === 0
        ? ''
        : productFeedDirective(block.feed);
    case 'video':
      return `<p style="margin:0 0 16px;"><a href="${escapeHtml(block.href)}"><img src="${escapeHtml(block.thumbnailSrc)}" alt="${escapeHtml(block.alt)}" width="552" style="max-width:100%;height:auto;display:block;border:0;"></a></p>`;
    default:
      return legacyBlockHtml(block);
  }
}

function legacyBlockHtml(
  block: Exclude<
    Block,
    { type: 'countdown' | 'coupon' | 'reviews' | 'video' | 'shared' | 'products' }
  >,
): string {
  switch (block.type) {
    case 'text':
      return `<p style="margin:0 0 16px;font-family:sans-serif;font-size:15px;line-height:1.5;color:#18181B;">${escapeHtml(block.text).replace(/\n/g, '<br>')}</p>`;
    case 'image':
      return `<img src="${escapeHtml(block.src)}" alt="${escapeHtml(block.alt)}" style="max-width:100%;display:block;margin:0 0 16px;">`;
    case 'button':
      return `<p style="margin:0 0 16px;"><a href="${escapeHtml(block.href)}" style="display:inline-block;background:#5457D6;color:#fff;padding:10px 18px;border-radius:8px;text-decoration:none;font-family:sans-serif;">${escapeHtml(block.label)}</a></p>`;
    case 'product':
      return `<table style="margin:0 0 16px;"><tr><td><img src="${escapeHtml(block.imageSrc)}" alt="" width="80" style="display:block;"></td><td style="padding-left:12px;font-family:sans-serif;"><div>${escapeHtml(block.name)}</div><div style="color:#52525B;">${escapeHtml(block.price)}</div><a href="${escapeHtml(block.href)}">View product</a></td></tr></table>`;
    case 'divider':
      return '<hr style="border:none;border-top:1px solid #E4E4E7;margin:16px 0;">';
    case 'footer': {
      // The preference page is where a recipient changes their language
      // and lists (Mikkel, 22 Oct 2026), so every footer links to it.
      const unsubscribe = `<a href="{{ preferences_url }}">Change language</a> &middot; <a href="${escapeHtml(block.unsubscribeHref)}">Unsubscribe</a>`;
      const links = block.legalNoticeHref?.trim()
        ? `<a href="${escapeHtml(block.legalNoticeHref.trim())}">Legal notice</a> &middot; ${unsubscribe}`
        : unsubscribe;
      return `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #E4E4E7;font-family:sans-serif;font-size:12px;color:#52525B;"><span class="${SENDER_DETAILS_CLASS}">${escapeHtml(block.companyAddress)}</span><br>${links}</div>`;
    }
  }
}

export function blocksToHtml(blocks: Block[]): string {
  return ensureDeviceCss(
    `<div style="max-width:600px;margin:0 auto;padding:24px;">${blocks.map(blockToHtml).join('\n')}</div>`,
  );
}
